import React from "react";
import { useMemo, useState } from "react";
import { FileText, Search } from "./icons";
import type { ImportEntry } from "./lib/import-types";
import { IMPORT_PRODUCTS, IMPORT_DATABASES } from "./lib/import-data";
import { formatCurrency, formatDate } from "./lib/store";
import { EntryStatusPill } from "./primitives";
import { Input, cx } from "./ui";
import {
  defaultRenderProductLink,
  type RenderProductLink,
} from "./routing";

interface Props {
  entries: ImportEntry[];
  showDatabaseColumn?: boolean;
  renderProductLink?: RenderProductLink;
}

const STATUS_FILTERS = ["all", "Cleared", "Pending", "Hold", "Detained"] as const;

export const EntriesTable: React.FC<Props> = ({
  entries,
  showDatabaseColumn,
  renderProductLink = defaultRenderProductLink,
}) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<string>("all");

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return entries.filter((e) => {
      if (status !== "all" && e.status !== status) return false;
      if (!ql) return true;
      const p = IMPORT_PRODUCTS.find((x) => x.id === e.productId);
      const hay = `${p?.name ?? ""} ${p?.sku ?? ""} ${e.entryNumber} ${e.portOfEntry}`.toLowerCase();
      return hay.includes(ql);
    });
  }, [entries, q, status]);

  return (
    <div className="ih-col ih-gap-3">
      <div className="ih-row-wrap ih-gap-2">
        <div className="ih-input-group" style={{ width: 260 }}>
          <span className="ih-input-group-icon"><Search size={14} /></span>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search entries…"
            className="ih-input-search ih-input-sm"
          />
        </div>
        <div className="ih-segmented">
          {STATUS_FILTERS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setStatus(k)}
              className={cx("ih-segmented-item", status === k && "is-active")}
            >
              {k === "all" ? "All" : k}
            </button>
          ))}
        </div>
        <span className="ih-entries-count ih-text-xs ih-text-muted ih-num">
          {filtered.length} packages
        </span>
      </div>

      <div className="ih-table-wrapper">
        <table className="ih-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Package / Entry #</th>
              <th>Entry Date</th>
              <th>Port of Entry</th>
              {showDatabaseColumn && <th>Database</th>}
              <th className="ih-text-right">Value</th>
              <th>Documents</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={showDatabaseColumn ? 8 : 7}
                  className="ih-empty-row"
                >
                  No entry packages match.
                </td>
              </tr>
            )}
            {filtered.map((e) => {
              const p = IMPORT_PRODUCTS.find((x) => x.id === e.productId);
              const db = IMPORT_DATABASES.find((d) => d.id === e.importDatabaseId);
              return (
                <tr key={e.id}>
                  <td>
                    {p ? (
                      renderProductLink({
                        id: p.id,
                        className: "ih-product-link",
                        children: (
                          <>
                            <div className="ih-product-link-name">{p.name}</div>
                            <div className="ih-mono ih-text-xs ih-text-muted">{p.sku}</div>
                          </>
                        ),
                      })
                    ) : (
                      <span className="ih-text-xs ih-text-muted ih-italic">
                        Unknown product
                      </span>
                    )}
                  </td>
                  <td className="ih-mono ih-text-xs">{e.entryNumber}</td>
                  <td className="ih-text-xs ih-num">{formatDate(e.entryDate)}</td>
                  <td className="ih-text-xs">{e.portOfEntry}</td>
                  {showDatabaseColumn && (
                    <td className="ih-text-xs">{db?.name ?? "—"}</td>
                  )}
                  <td className="ih-text-xs ih-num ih-text-right">
                    {formatCurrency(e.value)}
                  </td>
                  <td>
                    <span className="ih-row ih-gap-1 ih-text-xs ih-text-muted">
                      <FileText size={14} /> {e.documents.length}
                    </span>
                  </td>
                  <td><EntryStatusPill status={e.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
